/**
 * Person-mode Dashboard sections.
 *
 * Operates over the date range chosen in the Dashboard shell. Picks
 * a single person from the roster and shows their working-day stats
 * for the selected range, plus comparisons against their unit and
 * the company. Same workday-only normalization as the Company view.
 */
import { useMemo, useState } from "preact/hooks";
import type { JSX } from "preact";
import { state } from "../state/store";
import { activePeople, displayName, bySurname } from "../utils/names";
import { dateKey } from "../utils/dates";
import { getHoliday } from "../utils/holidays";
import { monthsInRange } from "../utils/workdays";
import { MONTHS } from "../config";
import { STATUS_LABEL, type StatusCode } from "../types";
import {
  computePerson,
  computeUnits,
  computeDaysInRange,
  computeRangeAggregate,
  workdayKeysFromDays,
  type PersonStat,
} from "./dashboardMetrics";
import { rangeLabel, type DateRange } from "./Dashboard";

const STATUS_COLOR: Record<StatusCode, string> = {
  P: "var(--s-presence)",
  T: "var(--s-trip)",
  R: "var(--s-remote)",
  M: "var(--s-milan)",
  O: "var(--s-out)",
  A: "var(--s-available)",
  V: "var(--s-rovereto)",
};

function pct(v: number, digits = 0): string {
  return `${(v * 100).toFixed(digits)}%`;
}

function diffPp(curr: number, ref: number): {
  text: string;
  dir: "up" | "down" | "flat";
} {
  const diff = (curr - ref) * 100;
  if (Math.abs(diff) < 0.5) return { text: "≈ avg", dir: "flat" };
  return {
    text: `${diff > 0 ? "+" : ""}${diff.toFixed(1)} pp vs avg`,
    dir: diff > 0 ? "up" : "down",
  };
}

export function DashboardPerson({
  range,
}: {
  range: DateRange;
}): JSX.Element {
  const s = state.value;

  // Roster ordered: active first, then former, surname order.
  const people = useMemo(() => {
    return Object.keys(s.roster).sort((a, b) => {
      const aActive = s.roster[a]?.active !== false;
      const bActive = s.roster[b]?.active !== false;
      if (aActive !== bActive) return aActive ? -1 : 1;
      return bySurname(a, b);
    });
  }, [s.roster]);

  const activeNames = useMemo(() => activePeople(s.roster), [s.roster]);

  const [picked, setPicked] = useState<string>(people[0] ?? "");
  const [filter, setFilter] = useState("");

  const filterLower = filter.trim().toLowerCase();
  const filteredPeople = filterLower
    ? people.filter((n) => {
        const info = s.roster[n];
        const hay = `${n} ${displayName(n)} ${info?.unit ?? ""} ${info?.role ?? ""} ${info?.email ?? ""}`.toLowerCase();
        return hay.includes(filterLower);
      })
    : people;

  const safePicked = filteredPeople.includes(picked)
    ? picked
    : filteredPeople[0] ?? "";

  // Company-wide baselines for the diff chips
  const companyDays = useMemo(
    () =>
      computeDaysInRange(
        range.startYear,
        range.startMonth,
        range.endYear,
        range.endMonth,
        activeNames,
        s.calendar,
        s.closures,
      ),
    [range, activeNames, s.calendar, s.closures],
  );
  const workdayKeys = useMemo(
    () => workdayKeysFromDays(companyDays),
    [companyDays],
  );
  const companyAgg = useMemo(
    () => computeRangeAggregate(companyDays, activeNames.length),
    [companyDays, activeNames.length],
  );
  const units = useMemo(
    () => computeUnits(workdayKeys, activeNames, s.roster, s.calendar),
    [workdayKeys, activeNames, s.roster, s.calendar],
  );

  if (!safePicked) {
    return (
      <div class="dashboard-person">
        <PersonControls
          filter={filter}
          setFilter={setFilter}
          options={filteredPeople}
          picked={safePicked}
          setPicked={setPicked}
          roster={s.roster}
        />
        <div class="dash-card">
          <div class="person-empty">
            Pick a teammate from the dropdown above to see their working-day stats.
          </div>
        </div>
      </div>
    );
  }

  const stat = computePerson(safePicked, workdayKeys, s.roster, s.calendar);
  const unitStat = units.find((u) => u.unit === stat.unit) ?? null;
  const personalCal = s.calendar[safePicked] ?? {};
  const personalNotes = s.notes[safePicked] ?? {};
  const personalMeetings = s.meetings[safePicked] ?? {};

  return (
    <div class="dashboard-person">
      <div class="dashboard-range-summary dashboard-range-summary-person">
        Showing <b>{rangeLabel(range)}</b> · {workdayKeys.length} working day{workdayKeys.length === 1 ? "" : "s"}
      </div>

      <PersonControls
        filter={filter}
        setFilter={setFilter}
        options={filteredPeople}
        picked={safePicked}
        setPicked={setPicked}
        roster={s.roster}
      />

      <PersonHeader stat={stat} roster={s.roster} />

      <PersonKPIs
        stat={stat}
        unitInOfficeShare={unitStat?.inOfficeShare ?? null}
        unitRemoteShare={unitStat?.remoteShare ?? null}
        unitTripShare={unitStat?.tripShare ?? null}
        unitCoverage={unitStat?.coverage ?? null}
        companyInOfficeShare={companyAgg.avgOccupancy}
        companyRemoteShare={companyAgg.avgRemoteShare}
        companyTripShare={companyAgg.avgTripShare}
        companyCoverage={companyAgg.coverage}
      />

      <PersonMiniCalendars
        range={range}
        cal={personalCal}
        closures={s.closures}
      />

      <PersonDayOfWeek stat={stat} />

      <PersonNotesTimeline
        range={range}
        notes={personalNotes}
        meetings={personalMeetings}
      />
    </div>
  );
}

/* ───── Controls (search + dropdown) ───── */

function PersonControls({
  filter, setFilter, options, picked, setPicked, roster,
}: {
  filter: string;
  setFilter: (v: string) => void;
  options: string[];
  picked: string;
  setPicked: (v: string) => void;
  roster: typeof state.value.roster;
}): JSX.Element {
  return (
    <div class="person-controls">
      <input
        type="text"
        class="person-filter"
        placeholder="Filter by name, unit, role, email…"
        value={filter}
        onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
      />
      <select
        class="person-select"
        value={picked}
        onChange={(e) => setPicked((e.target as HTMLSelectElement).value)}
      >
        {options.map((name) => {
          const r = roster[name];
          const isFormer = r?.active === false;
          const tag = isFormer ? " (Former)" : "";
          const unit = r?.unit ? ` — ${r.unit}` : "";
          return (
            <option key={name} value={name}>
              {displayName(name)}{unit}{tag}
            </option>
          );
        })}
        {options.length === 0 && <option value="">No matches</option>}
      </select>
    </div>
  );
}

/* ───── Header card ───── */

function PersonHeader({
  stat, roster,
}: {
  stat: PersonStat;
  roster: typeof state.value.roster;
}): JSX.Element {
  const r = roster[stat.name];
  const isFormer = r?.active === false;
  return (
    <div class="dash-card person-header">
      <div>
        <h2 class="person-name">
          {displayName(stat.name)}
          {isFormer && <span class="former-tag">Former</span>}
        </h2>
        <div class="person-meta">
          <span><b>Unit:</b> {stat.unit}</span>
          {stat.role && <span><b>Role:</b> {stat.role}</span>}
          {stat.email && <span><b>Email:</b> {stat.email}</span>}
        </div>
      </div>
    </div>
  );
}

/* ───── KPI tiles with unit/company comparisons ───── */

interface PKProps {
  stat: PersonStat;
  unitInOfficeShare: number | null;
  unitRemoteShare: number | null;
  unitTripShare: number | null;
  unitCoverage: number | null;
  companyInOfficeShare: number;
  companyRemoteShare: number;
  companyTripShare: number;
  companyCoverage: number;
}

function PersonKPIs(p: PKProps): JSX.Element {
  return (
    <div class="pulse-row">
      <PersonKPI
        label="In office (Lodi+Milan+Rovereto)"
        value={`${p.stat.inOffice} / ${p.stat.workingDays}`}
        sub={pct(p.stat.inOfficeShare)}
        unitDiff={
          p.unitInOfficeShare !== null
            ? diffPp(p.stat.inOfficeShare, p.unitInOfficeShare)
            : null
        }
        companyDiff={diffPp(p.stat.inOfficeShare, p.companyInOfficeShare)}
        accent="var(--s-presence)"
      />
      <PersonKPI
        label="Remote"
        value={`${p.stat.remote} / ${p.stat.workingDays}`}
        sub={pct(p.stat.remoteShare)}
        unitDiff={
          p.unitRemoteShare !== null
            ? diffPp(p.stat.remoteShare, p.unitRemoteShare)
            : null
        }
        companyDiff={diffPp(p.stat.remoteShare, p.companyRemoteShare)}
        accent="var(--s-remote)"
      />
      <PersonKPI
        label="Trip"
        value={`${p.stat.trip} / ${p.stat.workingDays}`}
        sub={pct(p.stat.tripShare)}
        unitDiff={
          p.unitTripShare !== null
            ? diffPp(p.stat.tripShare, p.unitTripShare)
            : null
        }
        companyDiff={diffPp(p.stat.tripShare, p.companyTripShare)}
        accent="var(--s-trip)"
      />
      <PersonKPI
        label="Out"
        value={`${p.stat.out} / ${p.stat.workingDays}`}
        sub={pct(p.stat.outShare)}
        accent="var(--s-out)"
      />
      <PersonKPI
        label="Available (A)"
        value={`${p.stat.available} / ${p.stat.workingDays}`}
        sub={pct(p.stat.availableShare)}
        accent="var(--s-available)"
      />
      <PersonKPI
        label="Schedule coverage"
        value={pct(p.stat.coverage)}
        sub={`${p.stat.filled} of ${p.stat.workingDays} working days filled`}
        unitDiff={
          p.unitCoverage !== null
            ? diffPp(p.stat.coverage, p.unitCoverage)
            : null
        }
        companyDiff={diffPp(p.stat.coverage, p.companyCoverage)}
        accent={
          p.stat.coverage >= 0.85
            ? "var(--s-presence)"
            : p.stat.coverage >= 0.6
              ? "var(--s-available)"
              : "var(--s-out)"
        }
      />
    </div>
  );
}

interface PKIProps {
  label: string;
  value: string;
  sub: string;
  accent: string;
  unitDiff?: { text: string; dir: "up" | "down" | "flat" } | null;
  companyDiff?: { text: string; dir: "up" | "down" | "flat" } | null;
}

function PersonKPI({
  label, value, sub, accent, unitDiff, companyDiff,
}: PKIProps): JSX.Element {
  return (
    <div class="pulse-card" style={`border-top:3px solid ${accent}`}>
      <div class="pulse-label">{label}</div>
      <div class="pulse-value">{value}</div>
      <div class="pulse-sub">
        <span>{sub}</span>
      </div>
      {(unitDiff || companyDiff) && (
        <div class="person-compare">
          {unitDiff && (
            <span class={`compare-chip compare-${unitDiff.dir}`} title="vs unit">
              U: {unitDiff.text}
            </span>
          )}
          {companyDiff && (
            <span class={`compare-chip compare-${companyDiff.dir}`} title="vs company">
              C: {companyDiff.text}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ───── Mini calendars for the picked person (one per month in range) ───── */

function PersonMiniCalendars({
  range, cal, closures,
}: {
  range: DateRange;
  cal: Record<string, StatusCode>;
  closures: typeof state.value.closures;
}): JSX.Element {
  const months = monthsInRange(
    range.startYear,
    range.startMonth,
    range.endYear,
    range.endMonth,
  );
  return (
    <div class="dash-card">
      <h3>
        Calendar
        {months.length > 1 && (
          <span class="dash-card-sub"> — {months.length} months</span>
        )}
      </h3>
      <div class="person-cal-list">
        {months.map(({ year, month }) => (
          <SinglePersonCalendar
            key={`${year}-${month}`}
            year={year}
            month={month}
            cal={cal}
            closures={closures}
            showTitle={months.length > 1}
          />
        ))}
      </div>
    </div>
  );
}

function SinglePersonCalendar({
  year, month, cal, closures, showTitle,
}: {
  year: number;
  month: number;
  cal: Record<string, StatusCode>;
  closures: typeof state.value.closures;
  showTitle: boolean;
}): JSX.Element {
  const n = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay();
  const lead = (firstDow + 6) % 7;
  const cells: Array<{ day: number; key: string } | null> = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= n; d++) {
    cells.push({ day: d, key: dateKey(year, month, d) });
  }
  return (
    <div class="person-cal">
      {showTitle && (
        <h4 class="person-cal-month">
          {MONTHS[month]} {year}
        </h4>
      )}
      <div class="person-cal-head">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} class="person-cal-hd">{d}</div>
        ))}
      </div>
      <div class="person-cal-grid">
        {cells.map((c, i) => {
          if (!c) return <div key={`pad-${i}`} class="person-cell pad" />;
          const code = cal[c.key];
          const dow = new Date(year, month, c.day).getDay();
          const isWE = dow === 0 || dow === 6;
          const holiday = getHoliday(c.key);
          const closure = closures[c.key];
          const cls: string[] = ["person-cell"];
          if (isWE) cls.push("we");
          if (holiday) cls.push("holiday");
          if (closure !== undefined) cls.push("closure");
          if (code) cls.push(`s-${code}`);
          const reason = closure !== undefined
            ? (closure || "Closure")
            : holiday?.name ?? null;
          const title = code
            ? `${c.day}: ${STATUS_LABEL[code]}${reason ? ` (${reason})` : ""}`
            : reason
              ? `${c.day}: ${reason}`
              : `${c.day}`;
          return (
            <div key={c.key} class={cls.join(" ")} title={title}>
              <span class="person-cell-day">{c.day}</span>
              {code && <span class="person-cell-code">{code}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ───── Day-of-week pattern for the person ───── */

function PersonDayOfWeek({ stat }: { stat: PersonStat }): JSX.Element {
  return (
    <div class="dash-card">
      <h3>Personal day-of-week pattern (in-office days)</h3>
      <div class="dow-chart">
        {stat.dayOfWeek.map((d) => {
          const heightPct = d.workingDays > 0 ? (d.avgOccupancy * 100) : 0;
          return (
            <div key={d.wd} class="dow-col">
              <div class="dow-bar-track">
                <div
                  class="dow-bar"
                  style={`height:${heightPct}%`}
                  title={`${d.label}: ${d.avgInOffice} of ${d.workingDays} working ${d.label}s in office (${pct(d.avgOccupancy)})`}
                />
              </div>
              <div class="dow-pct">{pct(d.avgOccupancy)}</div>
              <div class="dow-label">
                {d.label}
                <div class="dow-sub">{d.avgInOffice}/{d.workingDays}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div class="person-cal-legend">
        {(["P", "M", "V", "R", "T", "A", "O"] as StatusCode[]).map((c) => (
          <span key={c}>
            <i style={`background:${STATUS_COLOR[c]}`} />
            {c} — {STATUS_LABEL[c]}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ───── Notes / meetings timeline ───── */

function PersonNotesTimeline({
  range, notes, meetings,
}: {
  range: DateRange;
  notes: Record<string, string>;
  meetings: Record<string, string>;
}): JSX.Element {
  // Collect all days where this person has either a note or a meeting,
  // chronologically sorted, within the range.
  const months = monthsInRange(
    range.startYear,
    range.startMonth,
    range.endYear,
    range.endMonth,
  );
  const monthPrefixes = months.map(
    ({ year, month }) => `${year}-${(month + 1).toString().padStart(2, "0")}-`,
  );
  const keys = new Set<string>([...Object.keys(notes), ...Object.keys(meetings)]);
  const inRange = [...keys].filter((k) =>
    monthPrefixes.some((p) => k.startsWith(p)),
  );
  const days = inRange.sort();
  if (days.length === 0) {
    return (
      <div class="dash-card">
        <h3>Notes & meetings</h3>
        <div class="person-empty person-empty-soft">
          No notes or meetings recorded in this range.
        </div>
      </div>
    );
  }
  return (
    <div class="dash-card">
      <h3>Notes & meetings</h3>
      <ul class="person-timeline">
        {days.map((k) => {
          const [, mm, dd] = k.split("-");
          const monthLabel = MONTHS[Number(mm) - 1] ?? "";
          const note = notes[k];
          const meeting = meetings[k];
          return (
            <li key={k} class="person-timeline-item">
              <span class="person-timeline-day" title={k}>
                {Number(dd)}
                <span class="person-timeline-day-sub">
                  {monthLabel.slice(0, 3)}
                </span>
              </span>
              <div class="person-timeline-content">
                {meeting !== undefined && (
                  <div class="person-timeline-meeting">
                    🤝 {meeting || "(in-presence meeting flagged, no description)"}
                  </div>
                )}
                {note && <div class="person-timeline-note">{note}</div>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
